import React, { useState, useEffect, useRef, useContext } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { AuthContext } from "../../context/Auth/AuthContext";
import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";
import Drawer from "@material-ui/core/Drawer";
import IconButton from "@material-ui/core/IconButton";
import Divider from "@material-ui/core/Divider";
import CloseIcon from "@material-ui/icons/Close";

import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { sanitizeContactDynamicFields } from "../../utils/sanitizeContactDynamicFields";
import ContactFieldAutocomplete from "../ContactFieldAutocomplete";

const SITUACAO_FALLBACK = ["ENGAJADO", "NEUTRO", "ESPECULANDO"];
const CANAL_FALLBACK = [
  "1 Rls ORG", "2 Rls PAT", "3 CRI CAMP", "4 CRI TURB", "5 YouTube", "6 TikTok",
  "7 Linkedin", "8 Site", "9 E-commerce", "10 LP", "11 IND CLI", "12 DISP WTS",
  "13 ML", "14 IND TEC", "15 IND FOR", "16 E-mail MKT", "17 OUT CONS", "18 SDRS", "19 REPRE",
];
const REGIAO_FALLBACK = ["NORTE", "NORDESTE", "CENTRO OESTE", "SUL", "SUDESTE", "SP"];

const useStyles = makeStyles((theme) => ({
  drawerPaper: {
    width: "min(440px, 92vw)",
    maxWidth: "100%",
    display: "flex",
    flexDirection: "column",
    height: "100%",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1.5, 2),
    flexShrink: 0,
  },
  formBody: {
    flex: 1,
    overflow: "auto",
    minHeight: 0,
    padding: theme.spacing(2),
    paddingTop: theme.spacing(1),
  },
  actions: {
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    flexShrink: 0,
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
}));

const formatDateForInput = (date) => {
  if (!date) return "";
  if (typeof date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return date;
  }
  const d = new Date(date);
  if (isNaN(d.getTime())) return "";
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const parseDateFromInput = (dateString) => {
  if (!dateString) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return dateString;
  }
  if (dateString.includes("T")) {
    return dateString.split("T")[0];
  }
  return dateString;
};

/** Só campos adicionais são editáveis; o restante vem do GET e segue no PUT. */
const DrawerSchema = Yup.object().shape({
  name: Yup.string().max(250).nullable(),
  number: Yup.string().max(50).nullable(),
  email: Yup.string()
    .transform((v) => (v === "" || v == null ? null : v))
    .nullable()
    .email("Invalid email"),
  customDynamicFields: Yup.mixed().nullable(),
});

const ContactCustomFieldsDrawer = ({ open, onClose, contactId, onSaved }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const isMounted = useRef(true);
  const [loadingContact, setLoadingContact] = useState(false);
  const [contact, setContact] = useState(null);
  const [situacaoOptions, setSituacaoOptions] = useState([]);
  const [canalOptions, setCanalOptions] = useState([]);
  const [regiaoOptions, setRegiaoOptions] = useState([]);
  const [contactFieldGroups, setContactFieldGroups] = useState([]);
  const [customAttributeRows, setCustomAttributeRows] = useState([]);
  const [loadingCustomAttributes, setLoadingCustomAttributes] = useState(false);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!open || !contactId) return;

    const fetchContact = async () => {
      setLoadingContact(true);
      try {
        const { data } = await api.get(`/contacts/${contactId}`);
        if (!isMounted.current) return;
        setContact({
          ...data,
          birthDate: formatDateForInput(data.birthDate),
          situacao: data.situacao || "",
          canal: data.canal || "",
          regiao: data.regiao || "",
          campanha: data.campanha || "",
          produto: data.produto || "",
          customDynamicFields:
            data.customDynamicFields && typeof data.customDynamicFields === "object"
              ? data.customDynamicFields
              : {},
        });
      } catch (err) {
        toastError(err);
        if (isMounted.current) setContact(null);
      } finally {
        if (isMounted.current) setLoadingContact(false);
      }
    };

    fetchContact();
  }, [open, contactId]);

  useEffect(() => {
    if (!open || !user?.companyId) return;

    const loadCustomAttributes = async () => {
      setLoadingCustomAttributes(true);
      try {
        let groups = [];
        try {
          const groupsRes = await api.get("/contact-field-groups");
          groups = Array.isArray(groupsRes.data) ? groupsRes.data : [];
        } catch (groupsErr) {
          console.warn("[ContactCustomFieldsDrawer] contact-field-groups:", groupsErr);
          groups = [
            {
              id: -1,
              param: "SITUACAO",
              label: i18n.t("customContactFieldsPage.tabSituacao"),
              fieldKind: "SELECT_SINGLE",
              multiMax: null,
              sortOrder: 0,
            },
            {
              id: -2,
              param: "CANAL",
              label: i18n.t("customContactFieldsPage.tabCanal"),
              fieldKind: "SELECT_SINGLE",
              multiMax: null,
              sortOrder: 1,
            },
            {
              id: -3,
              param: "REGIAO",
              label: i18n.t("customContactFieldsPage.tabRegiao"),
              fieldKind: "SELECT_SINGLE",
              multiMax: null,
              sortOrder: 2,
            },
          ];
        }

        const [manageRes, situacaoResponse, canalResponse, regiaoResponse] = await Promise.all([
          api.get("/custom-attributes/manage"),
          api.get("/custom-attributes", { params: { context: "CONTACT", param: "SITUACAO" } }),
          api.get("/custom-attributes", { params: { context: "CONTACT", param: "CANAL" } }),
          api.get("/custom-attributes", { params: { context: "CONTACT", param: "REGIAO" } }),
        ]);

        if (isMounted.current) {
          setContactFieldGroups(groups);
          setCustomAttributeRows(Array.isArray(manageRes.data) ? manageRes.data : []);
          setSituacaoOptions(situacaoResponse.data || []);
          setCanalOptions(canalResponse.data || []);
          setRegiaoOptions(regiaoResponse.data || []);
        }
      } catch (err) {
        console.error("[ContactCustomFieldsDrawer] custom attributes:", err);
        if (isMounted.current) {
          toastError(err);
          setContactFieldGroups([]);
          setCustomAttributeRows([]);
          setSituacaoOptions([]);
          setCanalOptions([]);
          setRegiaoOptions([]);
        }
      } finally {
        if (isMounted.current) setLoadingCustomAttributes(false);
      }
    };

    loadCustomAttributes();
  }, [open, user?.companyId]);

  useEffect(() => {
    if (loadingCustomAttributes || !contactFieldGroups.length) return;
    setContact((prev) => {
      if (!prev?.customDynamicFields) return prev;
      const sanitized = sanitizeContactDynamicFields(
        prev.customDynamicFields,
        contactFieldGroups,
        customAttributeRows
      );
      if (
        JSON.stringify(sanitized) === JSON.stringify(prev.customDynamicFields)
      ) {
        return prev;
      }
      return { ...prev, customDynamicFields: sanitized };
    });
  }, [contactFieldGroups, customAttributeRows, loadingCustomAttributes]);

  const handleClose = () => {
    setContact(null);
    onClose();
  };

  const handleSave = async (values) => {
    const contactData = {
      ...values,
      disableBot: values.disableBot ?? contact?.disableBot,
      birthDate: parseDateFromInput(values.birthDate),
      customDynamicFields: sanitizeContactDynamicFields(
        values.customDynamicFields,
        contactFieldGroups,
        customAttributeRows
      ),
    };

    try {
      await api.put(`/contacts/${contactId}`, contactData);
      toast.success(i18n.t("contactModal.success"));
      if (typeof onSaved === "function") {
        onSaved({
          ...contactData,
          id: contactId,
          situacao: contactData.situacao,
          canal: contactData.canal,
          regiao: contactData.regiao,
          campanha: contactData.campanha,
          produto: contactData.produto,
          customDynamicFields: contactData.customDynamicFields,
        });
      }
      handleClose();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      ModalProps={{ keepMounted: false }}
      PaperProps={{
        className: classes.drawerPaper,
        style: { zIndex: 1400 },
      }}
    >
      <div className={classes.header}>
        <Typography variant="h6" component="span">
          {i18n.t("ticketInfo.customFieldsDrawerTitle")}
        </Typography>
        <IconButton edge="end" onClick={handleClose} aria-label="close">
          <CloseIcon />
        </IconButton>
      </div>
      <Divider />

      <div className={classes.formBody}>
        {loadingContact || !contact ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <Formik
            initialValues={contact}
            enableReinitialize
            validationSchema={DrawerSchema}
            onSubmit={async (values, actions) => {
              await handleSave(values);
              actions.setSubmitting(false);
            }}
          >
            {({ values, isSubmitting }) => (
              <Form id="contact-custom-fields-drawer-form">
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <ContactFieldAutocomplete
                      name="situacao"
                      label={i18n.t("contactModal.form.situacao")}
                      options={situacaoOptions.length ? situacaoOptions : SITUACAO_FALLBACK}
                      disabled={loadingCustomAttributes}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <ContactFieldAutocomplete
                      name="canal"
                      label={i18n.t("contactModal.form.canal")}
                      options={canalOptions.length ? canalOptions : CANAL_FALLBACK}
                      disabled={loadingCustomAttributes}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <ContactFieldAutocomplete
                      name="regiao"
                      label={i18n.t("contactModal.form.regiao")}
                      options={regiaoOptions.length ? regiaoOptions : REGIAO_FALLBACK}
                      disabled={loadingCustomAttributes}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Field
                      as={TextField}
                      label={i18n.t("contactModal.form.campanha")}
                      name="campanha"
                      variant="outlined"
                      fullWidth
                      margin="dense"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      label={i18n.t("contactModal.form.produto")}
                      name="produto"
                      variant="outlined"
                      fullWidth
                      margin="dense"
                    />
                  </Grid>

                  {(contactFieldGroups || [])
                    .filter((g) => !["SITUACAO", "CANAL", "REGIAO"].includes(g.param))
                    .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0) || a.id - b.id)
                    .map((g) => {
                      const opts = (customAttributeRows || [])
                        .filter((r) => r.param === g.param)
                        .map((r) => r.value);
                      const fname = `customDynamicFields.${g.param}`;
                      if (g.fieldKind === "TEXT") {
                        return (
                          <Grid item xs={12} sm={6} key={g.id}>
                            <Field
                              as={TextField}
                              label={g.label}
                              name={fname}
                              variant="outlined"
                              fullWidth
                              margin="dense"
                            />
                          </Grid>
                        );
                      }
                      if (g.fieldKind === "NUMBER") {
                        return (
                          <Grid item xs={12} sm={6} key={g.id}>
                            <Field
                              as={TextField}
                              label={g.label}
                              name={fname}
                              variant="outlined"
                              fullWidth
                              margin="dense"
                              inputProps={{ inputMode: "decimal" }}
                            />
                          </Grid>
                        );
                      }
                      if (g.fieldKind === "SELECT_SINGLE") {
                        return (
                          <Grid item xs={12} sm={6} key={g.id}>
                            <ContactFieldAutocomplete
                              name={fname}
                              label={g.label}
                              options={opts}
                              disabled={loadingCustomAttributes}
                            />
                          </Grid>
                        );
                      }
                      if (g.fieldKind === "SELECT_MULTI" || g.fieldKind === "SELECT_MULTI_LIMITED") {
                        return (
                          <Grid item xs={12} sm={6} key={g.id}>
                            <ContactFieldAutocomplete
                              name={fname}
                              label={g.label}
                              options={opts}
                              multiple
                              maxSelections={
                                g.fieldKind === "SELECT_MULTI_LIMITED"
                                  ? Number(g.multiMax) || 0
                                  : 0
                              }
                              disabled={loadingCustomAttributes}
                            />
                          </Grid>
                        );
                      }
                      return null;
                    })}
                </Grid>

                <div className={classes.actions}>
                  <Button onClick={handleClose} color="default" disabled={isSubmitting}>
                    {i18n.t("contactModal.buttons.cancel")}
                  </Button>
                  <Button
                    type="submit"
                    color="primary"
                    variant="contained"
                    className={classes.btnWrapper}
                    disabled={isSubmitting}
                  >
                    {i18n.t("contactModal.buttons.okEdit")}
                    {isSubmitting && (
                      <CircularProgress size={24} className={classes.buttonProgress} />
                    )}
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        )}
      </div>
    </Drawer>
  );
};

export default ContactCustomFieldsDrawer;
